import { useEffect } from "react";
import { useLanguage } from "@/lib/LanguageContext";

// PRODUCTION canonical origin. Preview deployments will still work — canonical points
// to the production domain to consolidate SEO signals (per Google's guidance).
const SITE_URL = "https://pacifickeymanagement.com";
const SITE_NAME = "Pacific Key Management";

// Default social-share hero (1200×630 recommended by OG). Replace this with a real
// branded card image at /brand/og-default.jpg once available.
const DEFAULT_OG_IMAGE =
    "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?auto=format&fit=crop&w=1200&h=630&q=85";

function setMeta(name, content, attr = "name") {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
    if (!href) return;
    const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

// Inject / replace a JSON-LD block by id, so pages can update their structured data
// without duplicating scripts across route changes.
function setJsonLd(id, data) {
    let el = document.getElementById(id);
    if (!data) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

/**
 * SEO hook — call once per page.
 * Sets: title, description, keywords, canonical, hreflang alternates,
 * Open Graph, Twitter card, and optional page-level JSON-LD structured data.
 *
 * @param {object}   opts
 * @param {string}   opts.title          Page-specific title (site name appended automatically)
 * @param {string}   opts.description    Meta description (150-160 chars ideal)
 * @param {string}   opts.path           Path portion of URL — used for canonical + og:url
 * @param {string=}  opts.image          OG/Twitter share image (1200×630 recommended)
 * @param {string=}  opts.keywords       Optional comma-separated keywords
 * @param {string=}  opts.type           Optional og:type override (default: "website")
 * @param {Array=}   opts.breadcrumbs    Optional [{ name, path }] — emits BreadcrumbList schema
 * @param {object=}  opts.schema         Optional additional JSON-LD (Service, Product, VacationRental, etc.)
 */
export default function useSEO({
    title,
    description,
    path = "/",
    image = DEFAULT_OG_IMAGE,
    imageAlt,
    robots,
    keywords,
    type = "website",
    breadcrumbs,
    schema,
}) {
    const { lang } = useLanguage();

    useEffect(() => {
        const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
        const url = `${SITE_URL}${path}`;
        document.title = fullTitle;

        setMeta("description", description);
        if (keywords) setMeta("keywords", keywords);
        setMeta("robots", robots || "index, follow, max-image-preview:large");

        // Open Graph
        setMeta("og:title", fullTitle, "property");
        setMeta("og:description", description, "property");
        setMeta("og:type", type, "property");
        setMeta("og:url", url, "property");
        setMeta("og:image", image, "property");
        setMeta("og:image:width", "1200", "property");
        setMeta("og:image:height", "630", "property");
        setMeta("og:image:alt", imageAlt || `${SITE_NAME} — Luxury Property Management, Los Cabos`, "property");
        setMeta("og:site_name", SITE_NAME, "property");
        setMeta("og:locale", lang === "es" ? "es_MX" : "en_US", "property");
        setMeta("og:locale:alternate", lang === "es" ? "en_US" : "es_MX", "property");

        // Twitter / X
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", fullTitle);
        setMeta("twitter:description", description);
        setMeta("twitter:image", image);
        setMeta("twitter:image:alt", imageAlt || `${SITE_NAME} — Luxury Property Management, Los Cabos`);

        // Canonical only. Hreflang alternates deliberately omitted because
        // English and Spanish currently resolve to the same URL — pointing
        // hreflang="en" and hreflang="es" at the same href is invalid per
        // Google's spec and provides no SEO value. Add dedicated /en/ and /es/
        // URL architecture first, then re-introduce hreflang.
        setLink("canonical", url);

        // BreadcrumbList JSON-LD
        if (breadcrumbs && breadcrumbs.length > 0) {
            setJsonLd("ld-breadcrumbs", {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbs.map((b, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: b.name,
                    item: `${SITE_URL}${b.path}`,
                })),
            });
        } else {
            setJsonLd("ld-breadcrumbs", null);
        }

        // Page-specific JSON-LD (service, vacation rental, etc.)
        if (schema) {
            setJsonLd("ld-page", schema);
        } else {
            setJsonLd("ld-page", null);
        }
    }, [title, description, path, image, imageAlt, robots, keywords, type, lang, breadcrumbs, schema]);
}

// Export constants so pages can build URLs consistently.
export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
